import {
  Button,
  Divider,
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
} from "@fluentui/react-components";
import {
  bundleIcon,
  CalendarFilled,
  CalendarRegular,
  ErrorCircleFilled,
} from "@fluentui/react-icons";
import { endOfDay, format, setHours, setMinutes, startOfDay } from "date-fns";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { GloBalDateFilter } from "../../../context/LayoutContext";
import { $crimson60, $sky60 } from "../../../cssjs/colors";
import {
  dateFnsFormat,
  getConvertedTime,
  getRollingFilterDateRange,
  validateCustomDateTimeValues,
  validateRollingTime,
} from "../../../utils/common/DateHelperFunctions";

import { DateTimeSettingsEnum } from "../../../enum/app-settings/AppSettingsEnum";
import {
  GlobalFilterOptionsEnum,
  GLOBAL_FILTER_OPTIONS_CONFIG,
  IAppliedFilterValues,
  TodayFilterOptionsEnum,
} from "../../../enum/global-filter/GlobalFilterEnum";
import { useApiResource } from "../../../hooks/useApiResource";
import { IApiResourceResponse } from "../../../models/app-settings/AppSettingsModels";
import { IGlobalDateFilter } from "../../../models/filters/IFilters";
import { extractTimeRange } from "../../../utils/app-settings/AppSettingsHelper";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import { AIRPORT_CODE_PARAMETER } from "../../../utils/common/Constants";
import TimeDisplay from "../../common/clock/TimeDisplay";
import { Timezone } from "../../common/layout/Layout";
import DateTimeFilterActions from "../date-filter/DateTimeFilterActions";
import CustomFilter from "./filter-options/CustomFilter";
import RollingFilterOption from "./filter-options/RollingFilter";
import TodayFilterOption from "./filter-options/TodayFilter";
import FilterOptionButtons from "./FilterOptionButtons";

const defaultAppliedFilterValues: IAppliedFilterValues = {
  selectedOption: GlobalFilterOptionsEnum.rolling,
  fromRollingHours: 0,
  fromRollingMinute: 0,
  toRollingHours: 0,
  toRollingMinute: 0,
  fromDate: new Date(),
  toDate: new Date(),
  todayOption: TodayFilterOptionsEnum.day,
};

const dateTimeSettingsURL = process.env.REACT_APP_API_DATE_TIME_CONFIG;
const baseURL = process.env.REACT_APP_API_BASE_URL;
const dateConfigUrl = `${baseURL}${dateTimeSettingsURL}`;

export interface IGlobalDateTimeFilterProps extends GloBalDateFilter {
  airportCode: string;
  timezone: Timezone;
  showGlobalDateTimeFilter: boolean;
  setGlobalStaticFilterDate: Dispatch<SetStateAction<IGlobalDateFilter>>;
}

const GlobalDateTimeFilter: React.FC<IGlobalDateTimeFilterProps> = ({
  airportCode,
  timezone,
  globalFilterDate,
  setGlobalFilterDate,
  showGlobalDateTimeFilter,
  currentTime,
  setCurrentTime,
  setSelectedFilter,
  setGlobalStaticFilterDate,
}) => {
  const classes = useStyles();
  const CalendarIcon = bundleIcon(CalendarFilled, CalendarRegular);
  const startDate = format(globalFilterDate.startDate, dateFnsFormat);
  const endDate = format(globalFilterDate.endDate, dateFnsFormat);
  const url = dateConfigUrl.replace(AIRPORT_CODE_PARAMETER, airportCode);

  //Used to update static global filter when timezone changes
  const timezoneRef = useRef<Timezone>(timezone);
  const { timeZoneId: airportTimeZone } = getAirportConfig(airportCode);

  const [isOpen, setIsOpen] = useState(false);

  const convertedFromDate = startOfDay(
    getConvertedTime(new Date(), timezone, airportTimeZone)
  );
  const convertedToDate = endOfDay(
    getConvertedTime(new Date(), timezone, airportTimeZone)
  );
  const [fromDate, setFromDate] = useState<Date>(convertedFromDate);
  const [toDate, setToDate] = useState<Date>(convertedToDate);
  const [todayOption, setTodayOption] = useState<string>(
    TodayFilterOptionsEnum.day
  );

  const [fromRollingHours, setFromRollingHours] = useState<number>(0);
  const [fromRollingMinute, setFromRollingMinute] = useState<number>(0);
  const [toRollingHours, setToRollingHours] = useState<number>(0);
  const [toRollingMinute, setToRollingMinute] = useState<number>(0);

  const [selectedFilterOption, setSelectedFilterOption] = useState<string>(
    GlobalFilterOptionsEnum.rolling
  );

  defaultAppliedFilterValues.fromDate = convertedFromDate;
  defaultAppliedFilterValues.toDate = convertedToDate;
  const [appliedFilterValues, setAppliedFilterValues] =
    useState<IAppliedFilterValues>(defaultAppliedFilterValues);

  const { data } = useApiResource<IApiResourceResponse>({
    queryKey: ["dateTimeKPISettings", airportCode],
    url: url,
  });

  const allGlobalFilterUpdateHandler = (dateRange: IGlobalDateFilter) => {
    setGlobalFilterDate(dateRange);
    setGlobalStaticFilterDate(dateRange);
  };

  const setGlobalFilterDefaults = (data: IApiResourceResponse | undefined) => {
    const { timeZoneId: newAirportTimeZone } = getAirportConfig(airportCode);
    const dateTimeSettings = data?.timeSettings || [];
    const selectedTab = data?.selectedConfig || DateTimeSettingsEnum.RollingHrs;
    const currentAirpotTime = getConvertedTime(
      new Date(),
      timezone,
      newAirportTimeZone
    );
    let startDate = startOfDay(currentAirpotTime);
    let endDate = endOfDay(currentAirpotTime);

    if (dateTimeSettings?.length > 0) {
      const selectedFilterOption =
        selectedTab === DateTimeSettingsEnum.RollingHrs
          ? GlobalFilterOptionsEnum.rolling
          : GlobalFilterOptionsEnum.custom;

      const rollingHrsSettings = dateTimeSettings.find(
        (item: any) => item.name === DateTimeSettingsEnum.RollingHrs
      );
      const customDateSettings = dateTimeSettings.find(
        (item: any) => item.name === DateTimeSettingsEnum.TimeDuration
      );

      const rollingTime = extractTimeRange(rollingHrsSettings);
      const durationTime = extractTimeRange(customDateSettings);
      startDate = setMinutes(
        setHours(convertedFromDate, durationTime.fromHours),
        durationTime.fromMinutes
      );
      endDate = setMinutes(
        setHours(convertedToDate, durationTime.toHours),
        durationTime.toMinutes
      );

      setAppliedFilterValues((prevValues: IAppliedFilterValues) => ({
        ...prevValues,
        fromRollingHours: rollingTime.fromHours,
        fromRollingMinute: rollingTime.fromMinutes,
        toRollingHours: rollingTime.toHours,
        toRollingMinute: rollingTime.toMinutes,
        fromDate: startDate,
        toDate: endDate,
        selectedOption: selectedFilterOption,
      }));

      setFromRollingHours(rollingTime.fromHours);
      setFromRollingMinute(rollingTime.fromMinutes);
      setToRollingHours(rollingTime.toHours);
      setToRollingMinute(rollingTime.toMinutes);
      setFromDate(startDate);
      setToDate(endDate);
      setSelectedFilterOption(selectedFilterOption);
      setSelectedFilter(selectedFilterOption);

      if (selectedFilterOption === GlobalFilterOptionsEnum.custom) {
        allGlobalFilterUpdateHandler({
          startDate: startDate,
          endDate: endDate,
        });
      } else if (selectedFilterOption === GlobalFilterOptionsEnum.rolling) {
        const rollingDateRange = getRollingFilterDateRange(
          {
            ...defaultAppliedFilterValues,
            fromRollingHours: rollingTime.fromHours,
            fromRollingMinute: rollingTime.fromMinutes,
            toRollingHours: rollingTime.toHours,
            toRollingMinute: rollingTime.toMinutes,
            selectedOption: GlobalFilterOptionsEnum.rolling,
            fromDate: startDate,
            toDate: endDate,
          },
          currentAirpotTime
        );
        if (rollingDateRange) {
          allGlobalFilterUpdateHandler(rollingDateRange);
        }
      }
    }
  };

  const rollingTimeProps = {
    fromRollingtime: {
      hour: fromRollingHours,
      minute: fromRollingMinute,
      setHour: setFromRollingHours,
      setMinute: setFromRollingMinute,
    },
    toRollingtime: {
      hour: toRollingHours,
      minute: toRollingMinute,
      setHour: setToRollingHours,
      setMinute: setToRollingMinute,
    },
  };

  const rollingHourError = validateRollingTime(
    fromRollingHours,
    fromRollingMinute,
    toRollingHours,
    toRollingMinute
  );

  const customDateTimeError = validateCustomDateTimeValues(fromDate, toDate);
  const disableApplyButton =
    (selectedFilterOption === GlobalFilterOptionsEnum.rolling &&
      rollingHourError.length > 0) ||
    (selectedFilterOption === GlobalFilterOptionsEnum.custom &&
      customDateTimeError.length > 0);

  useEffect(() => {
    // This effect runs when the popover is closed and sets the filter values to the applied values
    if (!isOpen) {
      setSelectedFilterOption(appliedFilterValues.selectedOption);
      setFromRollingHours(appliedFilterValues.fromRollingHours);
      setFromRollingMinute(appliedFilterValues.fromRollingMinute);
      setToRollingHours(appliedFilterValues.toRollingHours);
      setToRollingMinute(appliedFilterValues.toRollingMinute);
      setFromDate(appliedFilterValues.fromDate);
      setToDate(appliedFilterValues.toDate);
      setTodayOption(appliedFilterValues.todayOption);
    }
  }, [isOpen]);

  useEffect(() => {
    //Update global filter date on current time change for rolling option
    const dateRange = getRollingFilterDateRange(
      appliedFilterValues,
      currentTime
    );
    if (dateRange) {
      setGlobalFilterDate(dateRange);
    }

    if (timezoneRef.current !== timezone) {
      timezoneRef.current = timezone;

      //Update global filter date on current time change for today option - 24 hours selection
      if (
        appliedFilterValues.selectedOption === GlobalFilterOptionsEnum.today &&
        appliedFilterValues.todayOption === TodayFilterOptionsEnum.day
      ) {
        const startDate = startOfDay(currentTime);
        const endDate = endOfDay(currentTime);
        allGlobalFilterUpdateHandler({ startDate, endDate });
      }

      if (dateRange) {
        setGlobalStaticFilterDate(dateRange);
      }
    }
  }, [currentTime]);

  useEffect(() => {
    //Update global filter dynamic and static date on applied filter values change for both rolling and manual refresh
    const dateRange = getRollingFilterDateRange(
      appliedFilterValues,
      currentTime
    );
    if (dateRange) {
      allGlobalFilterUpdateHandler(dateRange);
    }
  }, [
    appliedFilterValues?.selectedOption,
    appliedFilterValues?.fromRollingHours,
    appliedFilterValues?.fromRollingMinute,
    appliedFilterValues?.toRollingHours,
    appliedFilterValues?.toRollingMinute,
    appliedFilterValues?.todayOption,
  ]);

  useEffect(() => {
    setGlobalFilterDefaults(data);
  }, [data]);

  //Change handler functions
  const filterChangeHandler = (id: string) => setSelectedFilterOption(id);

  const handleOpenChange = (_e: any, data: any) => {
    setIsOpen(data.open);
  };

  const handleDateChange = (newFromDate: Date, newToDate: Date) => {
    setFromDate(newFromDate);
    setToDate(newToDate);
  };

  //Action buttons handlers
  const onCancelHandler = (_event: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(false);
  };

  const onApplyHandler = (_event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedFilter(selectedFilterOption);

    let startDate: Date | null = null;
    let endDate: Date | null = null;

    switch (selectedFilterOption) {
      case GlobalFilterOptionsEnum.rolling: {
        setAppliedFilterValues((prev) => ({
          ...prev,
          selectedOption: selectedFilterOption,
          fromRollingHours,
          fromRollingMinute,
          toRollingHours,
          toRollingMinute,
        }));
        break;
      }

      case GlobalFilterOptionsEnum.today: {
        if (todayOption === TodayFilterOptionsEnum.day) {
          startDate = startOfDay(currentTime);
          endDate = endOfDay(currentTime);

          setAppliedFilterValues((prev) => ({
            ...prev,
            selectedOption: selectedFilterOption,
            fromDate: startOfDay(currentTime),
            toDate: endOfDay(currentTime),
            todayOption: TodayFilterOptionsEnum.day,
          }));
        } else if (todayOption === TodayFilterOptionsEnum.now) {
          setAppliedFilterValues((prev) => ({
            ...prev,
            selectedOption: selectedFilterOption,
            todayOption: TodayFilterOptionsEnum.now,
          }));
        }
        break;
      }

      case GlobalFilterOptionsEnum.custom: {
        startDate = fromDate;
        endDate = toDate;

        setAppliedFilterValues((prev) => ({
          ...prev,
          selectedOption: selectedFilterOption,
          fromDate,
          toDate,
        }));
        break;
      }
    }

    if (startDate && endDate) {
      allGlobalFilterUpdateHandler({ startDate, endDate });
    }

    setIsOpen(false);
  };

  const onResetHandler = (_event: React.MouseEvent<HTMLButtonElement>) => {
    setGlobalFilterDefaults(data);
  };

  return (
    <div className="d-flex" data-testid="global-date-filter">
      {showGlobalDateTimeFilter && (
        <>
          <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger>
              <Button
                appearance="transparent"
                onClick={() => setIsOpen(!isOpen)}
                icon={<CalendarIcon />}
                iconPosition="after"
                className={classes.dateButton}
              >
                {`${startDate} - ${endDate}`}
              </Button>
            </PopoverTrigger>
            <PopoverSurface className={classes.popOverContainer}>
              <FilterOptionButtons
                filterOptions={GLOBAL_FILTER_OPTIONS_CONFIG}
                selectedOption={selectedFilterOption}
                filterChangeHandler={filterChangeHandler}
              />
              {selectedFilterOption === GlobalFilterOptionsEnum.custom && (
                <CustomFilter
                  currentTime={currentTime}
                  fromDate={fromDate}
                  toDate={toDate}
                  onDateChange={handleDateChange}
                  errorMsg={customDateTimeError}
                />
              )}
              {selectedFilterOption === GlobalFilterOptionsEnum.rolling && (
                <RollingFilterOption
                  fromRollingtime={rollingTimeProps.fromRollingtime}
                  toRollingtime={rollingTimeProps.toRollingtime}
                  redirectionText={true}
                  padHour
                  padMinute
                />
              )}
              {selectedFilterOption === GlobalFilterOptionsEnum.today && (
                <TodayFilterOption
                  selectedOption={todayOption}
                  setTodayOption={setTodayOption}
                />
              )}
              {selectedFilterOption === GlobalFilterOptionsEnum.rolling &&
                rollingHourError.length > 0 && (
                  <div className={classes.errorMsg}>
                    <ErrorCircleFilled className={classes.icon} />
                    {rollingHourError}
                  </div>
                )}
              <DateTimeFilterActions
                onResetHandler={onResetHandler}
                onCancelHandler={onCancelHandler}
                onApplyHandler={onApplyHandler}
                diableApplyButton={disableApplyButton}
              />
            </PopoverSurface>
          </Popover>

          <Divider
            className={mergeClasses(classes.divider, "px-sm-1")}
            vertical
            appearance="strong"
          />
        </>
      )}
      <TimeDisplay
        currentTime={currentTime}
        setCurrentTime={setCurrentTime}
        airportTimeZone={airportTimeZone}
        timezone={timezone}
      />
    </div>
  );
};

export default GlobalDateTimeFilter;

const useStyles = makeStyles({
  dateButton: {
    color: $sky60,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1,
      border: `1px solid ${$sky60}`,
    },
  },
  popOverContainer: {
    width: "578px",
  },
  errorMsg: {
    color: $crimson60,
    fontStyle: "italic",
    padding: "10px",
    fontSize: "14px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "2px",
  },
  icon: {
    fontSize: "14px !important",
  },
  divider: {
    minHeight: "32px",
  },
});
